import React, { useEffect, useState } from "react";

/* ── helpers ─────────────────────────────────────────────── */

const cn = (...c) => c.filter(Boolean).join(" ");

function useScroll(threshold = 50) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [threshold]);
  return scrolled;
}

/* Animated hamburger ↔ X (pure CSS morph) */
function MenuToggleIcon({ open, className = "" }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(
        "transition-transform duration-300 ease-in-out",
        open && "-rotate-45",
        className
      )}
    >
      <path
        className={cn(
          "transition-all duration-300 ease-in-out",
          open
            ? "[stroke-dasharray:20_300] [stroke-dashoffset:-32.42px]"
            : "[stroke-dasharray:12_63]"
        )}
        d="M27 10 13 10C10.8 10 9 8.2 9 6 9 3.5 10.8 2 13 2 15.2 2 17 3.8 17 6L17 26C17 28.2 18.8 30 21 30 23.2 30 25 28.2 25 26 25 23.8 23.2 22 21 22L7 22"
      />
      <path d="M7 16 27 16" />
    </svg>
  );
}

/* ── data ────────────────────────────────────────────────── */

const links = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About Us", href: "#team" },
  { label: "Projects", href: "#projects" },
];

/* ── component ───────────────────────────────────────────── */

function Navbar() {
  const [open, setOpen] = useState(false);
  const scrolled = useScroll(50);

  /* body-scroll lock while mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* ─── Bar ──────────────────────────────────────────── */}
      <header
        style={{
          /* Solid dark when at the top — no compositing issues.
             Frosted glass pill when scrolled. */
          position: "fixed",
          top: scrolled && !open ? 16 : 0,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          width: scrolled && !open ? "min(720px, 94vw)" : "100%",
          maxWidth: scrolled && !open ? 720 : "100%",
          backgroundColor: scrolled
            ? "rgba(15, 12, 31, 0.72)"
            : "rgba(15, 12, 31, 1)",
          backdropFilter: scrolled ? "blur(24px) saturate(1.8)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(24px) saturate(1.8)" : "none",
          borderRadius: scrolled && !open ? 9999 : 0,
          border: scrolled
            ? "1px solid rgba(255,255,255,0.12)"
            : "none",
          borderBottom: !scrolled
            ? "1px solid rgba(255,255,255,0.06)"
            : undefined,
          boxShadow: scrolled
            ? "0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset"
            : "none",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <nav
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: scrolled ? 52 : 64,
            padding: scrolled ? "0 12px" : "0 16px",
            maxWidth: 1200,
            margin: "0 auto",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Logo */}
          <a
            href="#home"
            onClick={() => setOpen(false)}
            style={{ display: "flex", alignItems: "center" }}
          >
            <img
              src={process.env.PUBLIC_URL + "/images/landing/logo.svg"}
              alt="InTek Solutions"
              style={{
                height: scrolled ? 28 : 40,
                width: "auto",
                filter: "drop-shadow(0 4px 12px rgba(105,54,244,0.35))",
                transition: "height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </a>

          {/* Desktop links */}
          <div
            style={{
              alignItems: "center",
              gap: 4,
            }}
            className="hidden md:flex"
          >
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                style={{
                  color: "rgba(255,255,255,0.8)",
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  padding: "8px 16px",
                  borderRadius: 9999,
                  letterSpacing: "0.3px",
                  transition: "all 0.25s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                  e.currentTarget.style.color = "#ffffff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "rgba(255,255,255,0.8)";
                }}
              >
                {link.label}
              </a>
            ))}

            {/* CTA */}
            <a
              href="#contact1"
              style={{
                marginLeft: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 24px",
                borderRadius: 9999,
                background: "linear-gradient(135deg, #6936F4 0%, #244fa9 100%)",
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 600,
                textDecoration: "none",
                boxShadow: "0 6px 24px rgba(105,54,244,0.35)",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 10px 32px rgba(105,54,244,0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 24px rgba(105,54,244,0.35)";
              }}
            >
              Contact Us
              <span style={{ fontSize: 16 }}>→</span>
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex items-center justify-center md:hidden"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.1)",
              backgroundColor: "rgba(255,255,255,0.05)",
              color: "#ffffff",
              cursor: "pointer",
              transition: "background 0.25s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
            }}
          >
            <MenuToggleIcon open={open} className="w-5 h-5" />
          </button>
        </nav>
      </header>

      {/* ─── Mobile overlay ───────────────────────────────── */}
      <div
        className="md:hidden"
        style={{
          position: "fixed",
          inset: 0,
          top: 64,
          zIndex: 999,
          backgroundColor: "rgba(15, 12, 31, 0.98)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          display: "flex",
          flexDirection: "column",
          padding: 24,
          gap: 12,
          transform: open ? "translateX(0)" : "translateX(100%)",
          opacity: open ? 1 : 0,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: open ? "auto" : "none",
        }}
      >
        {links.map((link) => (
          <a
            key={link.label}
            href={link.href}
            onClick={() => setOpen(false)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px",
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.9)",
              textDecoration: "none",
              fontSize: 16,
              fontWeight: 500,
              transition: "all 0.25s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
              e.currentTarget.style.transform = "translateX(4px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
              e.currentTarget.style.transform = "translateX(0)";
            }}
          >
            <span>{link.label}</span>
            <span style={{ opacity: 0.4 }}>→</span>
          </a>
        ))}

        <a
          href="#contact1"
          onClick={() => setOpen(false)}
          style={{
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "16px 24px",
            borderRadius: 16,
            background: "linear-gradient(135deg, #6936F4 0%, #244fa9 100%)",
            color: "#ffffff",
            textDecoration: "none",
            fontSize: 16,
            fontWeight: 600,
            boxShadow: "0 8px 25px rgba(105,54,244,0.35)",
          }}
        >
          Contact Us <span style={{ fontSize: 18 }}>→</span>
        </a>
      </div>
    </>
  );
}

export default Navbar;